import React, { useState, useEffect } from "react";
import numeral from "numeral";
import { abbreviateNumber } from "pages/dealScreener/Email/constants";
import moment from "moment";
import styled from "styled-components";

import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  ValuationSensitivityChart,
  ValuationSensitivityToCapRateBox,
  ValuationSensitivityNOITable,
  PoweredByFooter
} from "../components";

import {
  Wrapper,
  SectionWrapper,
  DealInfoWrapper,
  TwoTextWrapper,
  DealInfoLeftWrapper,
  DealInfoRightWrapper,
  DealInfoChartBox,
  TitleText,
  DealInfoStyledBox,
  DealInfoBoxRow,
  DescriptionText,
  ValueText,
  ValuationSensitivitySectionWrapperTop,
  ValuationSensitivitySectionWrapperBottom,
  ValuationSensitivityPaddingWrapper,
  DottedLine,
  ValSensitivityToCapRateWrapper,
  ValSensitivitySubText,
  ValSensitivitySubTextBold,
  ValSensitivityToCapRateBoxesWrapper
} from "./styles";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ primaryColor }) => `#${primaryColor}`};
  gap: 1em;
`;

const DEFAULT_NOI_INCREASE_AMOUNT = 0.03;

const ValuationSensitivityPreview = ({ data }) => {
  const [maxPropertyValuation, setMaxPropertyValuation] = useState(0);
  const [minPropertyValuation, setMinPropertyValuation] = useState(0);
  const [noiIncreaseAmount, setNoiIncreaseAmount] = useState(DEFAULT_NOI_INCREASE_AMOUNT);

  const {
    noi,
    noiAsOfDate = null,
    dealName = "",
    loanBalance,
    prepaymentPenalty,
    closingDate,
    maturityDate,
    primaryColor,
    secondaryColor,
    footerText,
    valuationSensitivityToCapRate = [],
    valuationSensitivityToNoi = { isCustom: false, data: [] },
    logo = ""
  } = data;

  const { isCustom: isCustomSensitivity, data: noiSensitivityData, noiActualBumpAssumption } = valuationSensitivityToNoi;
  const dateFormat = "MM/DD/yyyy";

  useEffect(() => {
    if (valuationSensitivityToCapRate.length > 0) {
      const valuations = valuationSensitivityToCapRate.map(({ valuation }) => valuation);
      setMaxPropertyValuation(Math.max(...valuations));
      setMinPropertyValuation(Math.min(...valuations));
    }

    if (noiActualBumpAssumption) {
      setNoiIncreaseAmount(noiActualBumpAssumption);
    }
  }, [data]);

  return (
    <Wrapper>
      <Header
        companyLogo={logo}
        dealName={dealName}
        color={primaryColor}
        type="Valuation Sensitivity"
      />
      <Body primaryColor={primaryColor}>
        <SectionWrapper>
          <DealInfoWrapper>
            <DealInfoLeftWrapper>
              <TitleText>Deal Information</TitleText>
              <DealInfoStyledBox>
                <DealInfoBoxRow>
                  <DescriptionText>
                    <TwoTextWrapper>
                      <div>NOI</div>
                      {noiAsOfDate && <div>
                        (As of $
                        {moment(noiAsOfDate).utc().format(dateFormat)}
                        )
                      </div>}
                    </TwoTextWrapper>
                  </DescriptionText>
                  <ValueText>{numeral(noi).format("$0,0")}</ValueText>
                </DealInfoBoxRow>
                <DealInfoBoxRow>
                  <DescriptionText>Current Loan Balance</DescriptionText>
                  <ValueText>{numeral(loanBalance).format("$0,0")}</ValueText>
                </DealInfoBoxRow>
                <DealInfoBoxRow>
                  <DescriptionText>Prepayment Penalty</DescriptionText>
                  <ValueText>{numeral(prepaymentPenalty).format("$0,0")}</ValueText>
                </DealInfoBoxRow>
                <DealInfoBoxRow>
                  <DescriptionText>Close</DescriptionText>
                  <ValueText>{closingDate && moment(closingDate).utc().format(dateFormat)}</ValueText>
                </DealInfoBoxRow>
                <DealInfoBoxRow>
                  <DescriptionText>Maturity</DescriptionText>
                  <ValueText>{maturityDate && moment(maturityDate).utc().format(dateFormat)}</ValueText>
                </DealInfoBoxRow>
              </DealInfoStyledBox>
            </DealInfoLeftWrapper>
            <DealInfoRightWrapper>
              <DealInfoChartBox>
                <ValuationSensitivityChart color={secondaryColor} valuationSensitivityToCapRate={valuationSensitivityToCapRate} />
              </DealInfoChartBox>
            </DealInfoRightWrapper>
          </DealInfoWrapper>
        </SectionWrapper>
        <SectionWrapper style={{ paddingBottom: 0 }}>
          <ValuationSensitivitySectionWrapperTop>
            <TitleText>Valuation Sensitivity to Cap Rates</TitleText>
            <DottedLine />
            <ValSensitivityToCapRateWrapper>
              <ValSensitivitySubText>
                With a total
                <ValSensitivitySubTextBold>
                  {" "}
                  NOI of
                  {" "}
                  {numeral(noi).format("$0,0")}
                </ValSensitivitySubTextBold>
                , your property valuations could range from
                <ValSensitivitySubTextBold>
                  {" "}
                  $
                  {abbreviateNumber(maxPropertyValuation)}
                  {" "}
                  to $
                  {abbreviateNumber(minPropertyValuation)}
                  .
                </ValSensitivitySubTextBold>
              </ValSensitivitySubText>
              <ValSensitivityToCapRateBoxesWrapper>
                {valuationSensitivityToCapRate?.map(({ capRate, valuation }) => <ValuationSensitivityToCapRateBox color={primaryColor} key={`${capRate - valuation}`} capRate={capRate} valuation={valuation} />)}
              </ValSensitivityToCapRateBoxesWrapper>
            </ValSensitivityToCapRateWrapper>
          </ValuationSensitivitySectionWrapperTop>
          <ValuationSensitivitySectionWrapperBottom>
            <ValuationSensitivityPaddingWrapper style={{ padding: "0 1em" }}>
              <TitleText>Valuations With Increased NOI</TitleText>
              <DottedLine />
              {
                isCustomSensitivity ?
                  <ValSensitivitySubText>Below is a range of potential property valuations, assuming NOI follows your uploaded budget.</ValSensitivitySubText> :
                  <ValSensitivitySubText>
                    Below is a range of potential property valuations, assuming
                    <ValSensitivitySubTextBold>
                      {" "}
                      NOI increases of
                      {numeral(noiIncreaseAmount).format("0.00%")}
                      {" "}
                      YoY
                      {" "}
                    </ValSensitivitySubTextBold>
                    from the previously provided number.
                  </ValSensitivitySubText>
              }
            </ValuationSensitivityPaddingWrapper>
            <ValuationSensitivityNOITable rawData={noiSensitivityData} noiColor={primaryColor} />
          </ValuationSensitivitySectionWrapperBottom>
        </SectionWrapper>
      </Body>
      {footerText !== null ? <Footer footerText={footerText} /> : <PoweredByFooter />}
    </Wrapper>
  );
};

export default ValuationSensitivityPreview;
